/**
 * The town model both renderers draw. Only the look differs between renderers; the meaning of each piece is
 * fixed here, so each visual choice maps to the same real-world fact:
 *   district   = one moment (the place you heard the words); districts arrive in recording order
 *   tile       = one item (word, expression, line or suggested reply); home district = the first moment it came from
 *   stage      = its Leitner box: 0 fog (its first look not passed yet), then scaffolding, wood, stone, gold; 3+ is forged
 *   lit        = a lamp: a scored pass in the last LIT_DAYS days (an active learner's town glows at night)
 *   weathered  = ivy: overdue (forgetting shows as ivy growing with how late it is)
 *   bridge     = the item turned up in more than one moment, so it links those districts
 *   links      = word family (one lemma or base word, from the Wiktionary lookup) and "this phrase contains that word"
 *                (rules in word-links.ts)
 * Data comes from the learner's own town (`GET /api/town`, own-town.ts), a learner snapshot or a made-up learner (the
 * demo learners here, synthetic.ts for the phone check). The server sends materials, never box numbers: the client maps
 * them back to a stage for drawing and never shows the number.
 */

import type { TownProps } from './props';
import type { Street } from './streets';
import { wordLinks } from './word-links';

export type Kind = 'vocab' | 'expression' | 'line' | 'suggestion';

export interface SnapItem {
  key: string; ca: string; en: string; kind: Kind; latin?: string;
  box: number; learned: boolean; attempts: number; passes: number;
  practisedAt: string | null; dueAt: string; firstSeenAt: string;
  momentIds: string[];
  /** Moments (of momentIds) where it was recalled without help at least once; older snapshots lack it. */
  recalledIn?: string[];
  /** Arrival order among items of one moment (the server's order); ties fall back to firstSeenAt and the key. */
  seq?: number;
  /** Worked out by `GET /api/town` (v1 rules); computed from the dates above when absent. */
  lamp?: boolean; glow?: number; ivy?: boolean; overgrowth?: number; patina?: number; hardWon?: boolean;
  /** A friend's raid left damage on this building, independently of review condition. */
  cracked?: boolean;
  /** Authoritative maintenance state in the internal preview; absent from legacy V1 towns. */
  condition?: 'maintained' | 'neglected' | 'unproven';
}
/**
 * `lanes`/`paved`: the moment's lines, and those where the learner knows every word (v1 ticket 31); 0 when absent.
 * `streets`: its lines holding two or more of its own buildings, their keys in line order, paved once known.
 */
export interface SnapMoment { id: string; title: string; createdAt: string; lanes?: number; paved?: number; streets?: Street[] }
export interface TownSnapshot {
  id: string; label: string; language: string; now: string; moments: SnapMoment[]; items: SnapItem[];
  /** Scored answers in all (the sky's stars); summed from the items when absent. */
  answers?: number;
  /**
   * The signpost, lantern and founding stone (v1 ticket 40): the learner's own town has them, even with no district
   * (the empty land); synthetic and snapshot towns have none unless given, so they lay out as before.
   */
  props?: TownProps;
}

export type Stage = 0 | 1 | 2 | 3 | 4;

export interface Tile {
  key: string;
  item: SnapItem;
  /** Global arrival index. Place tiles in this order, each using only earlier tiles, and the map never reshuffles. */
  order: number;
  district: string;
  stage: Stage;
  forged: boolean;
  lit: boolean;
  weathered: boolean;
  /** Authoritative maintenance state, when supplied by the server. */
  condition?: 'maintained' | 'neglected' | 'unproven';
  /** Lamp brightness: 1 just practised, fading to 0.05 over LIT_DAYS; 0 when unlit. */
  glow: number;
  /** How late an overdue word is, 0..1 (three of its own intervals = 1); 0 unless weathered. */
  overgrowth: number;
  /** 0..1 for old, well-kept words: gold, first seen over a month ago, not overdue. */
  patina: number;
  /** Forged after many misses. */
  hardWon: boolean;
  /** Other districts this item also appeared in (bridge word when non-empty). */
  bridges: string[];
  /** Deterministic 0..1 noise for this tile, for variety that never changes between renders. */
  noise: number;
}

export interface District {
  id: string;
  title: string;
  createdAt: string;
  /** Arrival index of the district (0 = first moment recorded). */
  order: number;
  tiles: Tile[];
  practised: number;
  forged: number;
  lit: number;
  weathered: number;
  /** Share of tiles still in fog (first look not passed), 0..1. */
  fog: number;
  /** The moment's lines, and how many of them pave a lane: lines where the learner knows every word (v1 ticket 31). */
  lanes: number;
  paved: number;
  /** Its streets (streets.ts): the lines between its own buildings, chalked, or paved once every word is known. */
  streets: Street[];
}

export interface Link { a: string; b: string; kind: 'family' | 'contains' }
/** A road between two districts: the items they share (bridges) and how many cross links join them. */
export interface Road { a: string; b: string; shared: string[]; related: number }

export interface Town {
  snapshot: TownSnapshot;
  now: Date;
  /** Districts in arrival order. */
  districts: District[];
  /** Tiles in arrival order (same as `order`). */
  tiles: Tile[];
  byKey: Map<string, Tile>;
  byDistrict: Map<string, District>;
  links: Link[];
  roads: Road[];
  /** Battle mode (v1 ticket 51): this district's paved streets stand as walls, its lit lamps as watchtowers. */
  battle?: { district: string };
  stats: { words: number; districts: number; practised: number; forged: number; lit: number; weathered: number; fog: number; bridges: number };
}

export const LIT_DAYS = 7;
const BOX_DAYS = [1, 1, 3, 7, 14];
const DAY = 86_400_000;

// ---- deterministic randomness -------------------------------------------------------------------------

/** FNV-1a 32-bit. */
export function hash(s: string): number {
  let h = 0x811c9dc5;
  for (let i = 0; i < s.length; i++) { h ^= s.charCodeAt(i); h = Math.imul(h, 0x01000193); }
  return h >>> 0;
}
/** Seeded PRNG (mulberry32) → () => 0..1. */
export function rng(seed: number | string): () => number {
  let a = typeof seed === 'string' ? hash(seed) : seed >>> 0;
  return () => {
    a = (a + 0x6d2b79f5) >>> 0;
    let t = a;
    t = Math.imul(t ^ (t >>> 15), t | 1);
    t ^= t + Math.imul(t ^ (t >>> 7), t | 61);
    return ((t ^ (t >>> 14)) >>> 0) / 4294967296;
  };
}

// ---- building the town --------------------------------------------------------------------------------

const norm = (s: string) => s.normalize('NFD').replace(/\p{M}/gu, '').toLowerCase().replace(/[^\p{L}\p{N}]+/gu, ' ').trim();
/** A word without its Catalan article, so "el préssec" and "préssec" count as the same single word. */
const core = (s: string) => norm(s).replace(/^(el|la|els|les|l|un|una|uns|unes|en|na|es|sa) /, '');

/**
 * Build the town as it stood after the first `upToMoments` moments (all when omitted).
 * Tiles and districts only ever depend on earlier arrivals, so a variant that places them incrementally in
 * `order` draws the same place for the same word at every point of the growth slider.
 */
export function buildTown(snap: TownSnapshot, upToMoments?: number): Town {
  const now = new Date(snap.now);
  const moments = [...snap.moments].sort((a, b) => a.createdAt.localeCompare(b.createdAt) || a.id.localeCompare(b.id));
  const shown = moments.slice(0, upToMoments ?? moments.length);
  const rank = new Map(shown.map((m, i) => [m.id, i]));

  const items = snap.items
    .map((it) => ({ it, ids: it.momentIds.filter((id) => rank.has(id)).sort((a, b) => rank.get(a)! - rank.get(b)!) }))
    .filter((x) => x.ids.length > 0)
    .sort((x, y) => rank.get(x.ids[0])! - rank.get(y.ids[0])! || (x.it.seq ?? 0) - (y.it.seq ?? 0)
      || x.it.firstSeenAt.localeCompare(y.it.firstSeenAt) || x.it.key.localeCompare(y.it.key));

  const districts: District[] = shown.map((m, i) => ({
    id: m.id, title: m.title, createdAt: m.createdAt, order: i, tiles: [], practised: 0, forged: 0, lit: 0, weathered: 0, fog: 0,
    lanes: m.lanes ?? 0, paved: Math.min(m.paved ?? 0, m.lanes ?? 0), streets: m.streets ?? [],
  }));
  const byDistrict = new Map(districts.map((d) => [d.id, d]));

  const tiles: Tile[] = items.map(({ it, ids }, order) => {
    // Fog is box 0, whatever the attempts: a missed or hinted first look keeps the word in the fog (ticket 07).
    const stage = Math.max(0, Math.min(4, Math.round(it.box))) as Stage;
    const practised = stage > 0;
    const w = weather(it, stage, now.getTime());
    const tile: Tile = {
      key: it.key, item: it, order, district: ids[0], stage, forged: stage >= 3, ...w,
      ...(it.condition ? { condition: it.condition } : {}),
      bridges: ids.slice(1), noise: rng(it.key)(),
    };
    const d = byDistrict.get(tile.district)!;
    d.tiles.push(tile);
    if (practised) d.practised++;
    if (tile.forged) d.forged++;
    if (tile.lit) d.lit++;
    if (tile.weathered) d.weathered++;
    return tile;
  });
  for (const d of districts) d.fog = d.tiles.length ? 1 - d.practised / d.tiles.length : 0;
  const byKey = new Map(tiles.map((t) => [t.key, t]));

  // Links: word families (one lemma or base word) and containment (a phrase holds a word), see town-links.ts.
  const links = wordLinks(tiles, snap.language);

  // Roads between districts: shared items plus cross-district links.
  const roadMap = new Map<string, Road>();
  const road = (x: string, y: string) => {
    const [a, b] = byDistrict.get(x)!.order < byDistrict.get(y)!.order ? [x, y] : [y, x];
    const k = `${a}|${b}`;
    if (!roadMap.has(k)) roadMap.set(k, { a, b, shared: [], related: 0 });
    return roadMap.get(k)!;
  };
  for (const t of tiles) for (const other of t.bridges) road(t.district, other).shared.push(t.key);
  for (const l of links) {
    const da = byKey.get(l.a)!.district, db = byKey.get(l.b)!.district;
    if (da !== db) road(da, db).related++;
  }

  const count = (f: (t: Tile) => boolean) => tiles.filter(f).length;
  return {
    snapshot: snap, now, districts, tiles, byKey, byDistrict, links, roads: [...roadMap.values()],
    stats: {
      words: tiles.length, districts: districts.length,
      practised: count((t) => t.stage > 0), forged: count((t) => t.forged), lit: count((t) => t.lit),
      weathered: count((t) => t.weathered), fog: count((t) => t.stage === 0), bridges: count((t) => t.bridges.length > 0),
    },
  };
}

/**
 * Lamp, ivy, patina and hard-won for one item: the server's values when it sent them (`GET /api/town`), else the
 * same v1 rules from the snapshot's dates. Fog shows nothing else: no lamp, no ivy.
 */
function weather(it: SnapItem, stage: Stage, now: number): Pick<Tile, 'lit' | 'glow' | 'weathered' | 'overgrowth' | 'patina' | 'hardWon'> {
  if (stage === 0) return { lit: false, glow: 0, weathered: false, overgrowth: 0, patina: 0, hardWon: false };
  const last = it.practisedAt ? new Date(it.practisedAt).getTime() : null;
  const lit = it.lamp ?? (last !== null && now - last <= LIT_DAYS * DAY);
  const glow = it.glow ?? (lit && last !== null ? Math.max(0.05, 1 - (now - last) / (LIT_DAYS * DAY)) : lit ? 1 : 0);
  const over = now - new Date(it.dueAt).getTime();
  const weathered = it.ivy ?? over >= 0;
  const overgrowth = it.overgrowth ?? (weathered ? clamp01(over / (BOX_DAYS[stage] * DAY) / 3) : 0);
  const age = (now - new Date(it.firstSeenAt).getTime()) / DAY;
  const patina = it.patina ?? (stage === 4 && !weathered ? clamp01((age - 30) / 90) : 0);
  const hardWon = it.hardWon ?? (stage >= 3 && it.attempts - it.passes >= 3);
  return { lit, glow: lit ? glow : 0, weathered, overgrowth: weathered ? overgrowth : 0, patina, hardWon };
}
const clamp01 = (x: number) => Math.max(0, Math.min(1, x));

// ---- made-up learners (no real data) -------------------------------------------------------------------

/**
 * PROJECTION, made up: the same words and moments after ~3 months of daily reviewing. Practised words mostly
 * climb to boxes 3–4, about half the fog gets worked on, some words slip and weather. Deterministic per word.
 */
export function project(snap: TownSnapshot, days = 90): TownSnapshot {
  const now = new Date(new Date(snap.now).getTime() + days * DAY);
  const pick = (r: number, w: number[]) => { let acc = 0; for (let i = 0; i < w.length; i++) { acc += w[i]; if (r < acc) return i + 1; } return w.length; };
  const born = new Map(snap.moments.map((m) => [m.id, m.createdAt]));
  /** Made-up recalls from that practice (own stream per word): its home moment once past box 1 (else a coin flip),
   *  each other moment more often once forged. Real recalls stay. */
  const recalls = (it: SnapItem, box: number) => {
    const rr = rng(`${it.key}|recall`);
    const ids = [...it.momentIds].sort((a, b) => (born.get(a) ?? '').localeCompare(born.get(b) ?? '') || a.localeCompare(b));
    return ids.filter((id, i) => { const hit = i === 0 ? box >= 2 || rr() < 0.5 : rr() < (box >= 3 ? 0.55 : 0.25); return hit || !!it.recalledIn?.includes(id); });
  };
  return {
    ...snap, id: `${snap.id}+${days}`, label: `${snap.label} · +3 months (made up)`, now: now.toISOString(),
    items: snap.items.map((it) => {
      const r = rng(`${it.key}|proj`);
      const worked = it.attempts > 0 || r() < 0.55;
      if (!worked) return it;
      const box = Math.min(4, it.attempts > 0 ? pick(r(), [0.08, 0.12, 0.25, 0.25, 0.3]) : pick(r(), [0.2, 0.25, 0.25, 0.15, 0.15]));
      // A daily reviewer keeps most words on schedule (last practised within their interval); ~12% slip.
      const slipping = r() < 0.12;
      const ago = slipping ? BOX_DAYS[box] * 2 + 2 + r() * 10 : r() * BOX_DAYS[box];
      const practisedAt = new Date(now.getTime() - ago * DAY);
      const dueAt = new Date(practisedAt.getTime() + BOX_DAYS[box] * DAY);
      return { ...it, box, attempts: Math.max(it.attempts, 3 + box * 2), passes: Math.max(it.passes, 2 + box), learned: box >= 3, practisedAt: practisedAt.toISOString(), dueAt: dueAt.toISOString(), recalledIn: recalls(it, box) };
    }),
  };
}

/** Built-in demo learner, so the prototype runs without local data: a different journey (a newcomer to a city). */
export function demoLearner(): TownSnapshot {
  const scenes: [string, [string, string][]][] = [
    ['Buying fruit at the market', [['préssec', 'peach'], ['poma', 'apple'], ['maduixes', 'strawberries'], ['quant val?', 'how much is it?'], ['un quilo', 'a kilo'], ['la parada', 'the stall'], ['barat', 'cheap'], ['em poses mig quilo?', 'can you give me half a kilo?'], ['car', 'expensive'], ['res més?', 'anything else?']]],
    ['Missing the train at Sants', [['el tren', 'the train'], ['l\'andana', 'the platform'], ['el bitllet', 'the ticket'], ['ha marxat', 'it has left'], ['el pròxim', 'the next one'], ['amb retard', 'late'], ['la via', 'the track'], ['quina mala sort!', 'what bad luck!'], ['d\'aquí a deu minuts', 'in ten minutes'], ['perdut', 'lost'], ['de seguida', 'right away']]],
    ['Coffee with a neighbour', [['un tallat', 'a cortado'], ['el compte', 'the bill'], ['no passa res', 'no worries'], ['el veí', 'the neighbour'], ['què tal?', 'how are you?'], ['fa molta calor', 'it is very hot'], ['convido jo', 'my treat'], ['la terrassa', 'the terrace'], ['compte!', 'careful!'], ['el bar', 'the bar']]],
    ['At the pharmacy', [['la farmàcia', 'the pharmacy'], ['em fa mal el cap', 'I have a headache'], ['la recepta', 'the prescription'], ['una pastilla', 'a pill'], ['cada vuit hores', 'every eight hours'], ['la tos', 'the cough'], ['refredat', 'a cold'], ['que et milloris', 'get well'], ['quant val?', 'how much is it?'], ['la sopa', 'the soup']]],
    ['Football at the bar', [['el partit', 'the match'], ['gol!', 'goal!'], ['l\'àrbitre', 'the referee'], ['la samarreta', 'the shirt'], ['perdre', 'to lose'], ['guanyar', 'to win'], ['empat', 'a draw'], ['quin partidàs!', 'what a match!'], ['el bar', 'the bar'], ['una canya', 'a small beer']]],
    ['Looking for a flat', [['el pis', 'the flat'], ['el lloguer', 'the rent'], ['l\'habitació', 'the room'], ['la cuina', 'the kitchen'], ['moblat', 'furnished'], ['el propietari', 'the landlord'], ['la fiança', 'the deposit'], ['de seguida', 'right away'], ['el balcó', 'the balcony']]],
    ['Dinner with friends', [['bon profit', 'enjoy your meal'], ['el sopar', 'dinner'], ['la truita', 'the omelette'], ['pa amb tomàquet', 'bread with tomato'], ['el vi', 'the wine'], ['la sobretaula', 'chatting after the meal'], ['estic ple', 'I am full'], ['salut!', 'cheers!'], ['el compte', 'the bill']]],
  ];
  const start = new Date('2026-06-01T09:00:00Z').getTime();
  const now = new Date(start + 115 * DAY);
  const moments: SnapMoment[] = scenes.map(([title], i) => ({ id: `demo-${i}`, title, createdAt: new Date(start + i * 14 * DAY).toISOString() }));
  const byWord = new Map<string, SnapItem>();
  scenes.forEach(([, words], i) => words.forEach(([ca, en], j) => {
    const key = `demo:${ca}`;
    const seen = byWord.get(key);
    if (seen) { seen.momentIds.push(moments[i].id); return; }
    const r = rng(key);
    const age = (115 - i * 14) / 115;
    const worked = r() < 0.35 + 0.6 * age;
    const box = worked ? Math.min(4, 1 + Math.floor(r() * 5 * age + 0.5)) : 0;
    const slipping = r() < 0.15;
    const practisedAt = worked ? new Date(now.getTime() - (slipping ? BOX_DAYS[box] * 2 + 3 : r() * BOX_DAYS[box]) * DAY) : null;
    const dueAt = new Date((practisedAt?.getTime() ?? now.getTime()) + BOX_DAYS[box] * DAY);
    const multi = core(ca).includes(' ');
    byWord.set(key, {
      key, ca, en, kind: multi ? 'expression' : 'vocab',
      box, learned: box >= 3, attempts: worked ? 2 + box * 2 : 0, passes: worked ? 1 + box : 0,
      practisedAt: practisedAt?.toISOString() ?? null, dueAt: dueAt.toISOString(),
      firstSeenAt: new Date(new Date(moments[i].createdAt).getTime() + j * 1000).toISOString(), momentIds: [moments[i].id],
      recalledIn: worked && (box >= 2 || r() < 0.5) ? [moments[i].id] : [],
    });
  }));
  // Hand-set words, so the demo shows each bridge material and two ferries: [word, box (0 = never practised), scenes recalled in].
  // (At the market, 'em poses mig quilo?' faces 'un quilo' across the lane: the two are linked, so an arch joins them.)
  const set: [string, number, number[]][] = [
    ['el compte', 4, [2, 6]], // forged and recalled in both moments: stone
    ['quant val?', 3, [0]], // forged, but recalled only at the market: still wood
    ['el bar', 2, [4]], // recalled once: wood
    ['de seguida', 0, []], // heard only: rope
    ['perdut', 3, [1]], ['perdre', 2, [4]], // one lemma, both recalled: a ferry
    ['la sopa', 2, [3]], ['el sopar', 2, [6]], // one base word (sopa → sopar), both recalled: a ferry
  ];
  for (const [ca, box, at] of set) {
    const it = byWord.get(`demo:${ca}`)!, b = box;
    const practisedAt = box ? new Date(now.getTime() - DAY) : null;
    Object.assign(it, {
      box: b, learned: b >= 3, attempts: box ? 2 + b * 2 : 0, passes: box ? 1 + b : 0, practisedAt: practisedAt?.toISOString() ?? null,
      dueAt: new Date((practisedAt?.getTime() ?? now.getTime()) + BOX_DAYS[b] * DAY).toISOString(), recalledIn: at.map((i) => moments[i].id),
    });
  }
  return { id: 'demo', label: 'Demo learner (made up)', language: 'ca', now: now.toISOString(), moments, items: [...byWord.values()] };
}

/**
 * Built-in Persian demo learner (made up): a newcomer in Isfahan, with a trip to a Shiraz garden. Persian script in
 * `ca`, Finglish in `latin`. Each word: [Persian, Finglish, English, kind ('' = vocab or expression by spaces),
 * box (omitted = the same made-up practice as the Catalan demo; 0 = heard, never practised)]. Suggestions build
 * bazaars, lines row houses, expressions landmarks; the hand-set boxes make sure each shows at its later stages.
 */
export function demoFa(): TownSnapshot {
  const faScenes: [string, [string, string, string, string?, number?][]][] = [
    ['Fruit at the bazaar', [['بازار', 'bâzâr', 'the bazaar', '', 4], ['انار', 'anâr', 'a pomegranate'], ['هندوانه', 'hendevâne', 'a watermelon'], ['چنده؟', 'chande?', 'how much is it?', 'line'], ['یه کیلو', 'ye kilo', 'one kilo'], ['ارزون', 'arzun', 'cheap'], ['گرون', 'gerun', 'expensive'], ['تخفیف بدین', 'takhfif bedin', 'give me a discount', 'suggestion', 4], ['دیگه چی؟', 'dige chi?', 'anything else?', 'line'], ['خریدن', 'kharidan', 'to buy']]],
    ['A taxi to Naqsh-e Jahan', [['تاکسی', 'tâksi', 'the taxi'], ['دربست', 'darbast', 'a private hire'], ['میدان', 'meydân', 'the square', '', 4], ['مستقیم برید', 'mostaghim berid', 'go straight on', 'line'], ['همین‌جا', 'haminjâ', 'right here'], ['کرایه', 'kerâye', 'the fare'], ['نگه دارید', 'negah dârid', 'please stop here', 'suggestion', 4], ['دور', 'dur', 'far'], ['نزدیک', 'nazdik', 'near'], ['رفتن', 'raftan', 'to go']]],
    ['Tea in the neighbour\'s courtyard', [['چای', 'chây', 'the tea'], ['همسایه', 'hamsâye', 'the neighbour'], ['حیاط', 'hayât', 'the courtyard', '', 4], ['حوض', 'howz', 'the pool'], ['بفرمایید', 'befarmâyid', 'please, go ahead', 'line'], ['خیلی ممنون', 'kheyli mamnun', 'thank you very much', '', 4], ['تعارف نکن', 'taârof nakon', 'don\'t stand on ceremony', 'suggestion', 4], ['گل', 'gol', 'a flower'], ['خسته نباشید', 'khaste nabâshid', 'may you not be tired']]],
    ['At the pharmacy', [['داروخانه', 'dârukhâne', 'the pharmacy'], ['سرم درد می‌کنه', 'saram dard mikone', 'I have a headache', 'line'], ['نسخه', 'noskhe', 'the prescription'], ['قرص', 'ghors', 'a pill'], ['هر هشت ساعت', 'har hasht sâat', 'every eight hours'], ['سرماخوردگی', 'sarmâkhordegi', 'a cold'], ['بلا دور باشه', 'balâ dur bâshe', 'get well soon'], ['چنده؟', 'chande?', 'how much is it?', 'line'], ['سوپ', 'sup', 'the soup']]],
    ['Evening in a Shiraz garden', [['باغ', 'bâgh', 'the garden', '', 4], ['سرو', 'sarv', 'a cypress'], ['گل', 'gol', 'a flower'], ['آب', 'âb', 'the water'], ['خنک', 'khonak', 'cool', '', 4], ['قشنگ', 'ghashang', 'beautiful', '', 4], ['قدم بزنیم', 'ghadam bezanim', 'let\'s go for a walk', 'suggestion', 3], ['غروب', 'ghorub', 'the sunset'], ['عکس بگیریم', 'aks begirim', 'let\'s take a photo', 'suggestion', 4], ['دیدن', 'didan', 'to see']]],
    ['Looking for a flat', [['خونه', 'khune', 'the house'], ['اجاره', 'ejâre', 'the rent'], ['اتاق', 'otâgh', 'the room'], ['آشپزخونه', 'âshpazkhune', 'the kitchen'], ['صاحب‌خونه', 'sâhebkhune', 'the landlord'], ['همین‌جا', 'haminjâ', 'right here'], ['کی می‌تونم ببینم؟', 'key mitunam bebinam?', 'when can I see it?', 'suggestion'], ['روشن', 'roshan', 'bright']]],
    ['Dinner with friends', [['نوش جان', 'nush-e jân', 'enjoy your meal', '', 4], ['شام', 'shâm', 'the dinner'], ['برنج', 'berenj', 'the rice'], ['ته‌دیگ', 'tahdig', 'the crispy rice'], ['خورش', 'khoresh', 'the stew'], ['سیرم', 'siram', 'I\'m full', 'line'], ['خوشمزه', 'khoshmaze', 'delicious'], ['دستت درد نکنه', 'dastet dard nakone', 'thanks for the trouble'], ['بفرمایید', 'befarmâyid', 'please, go ahead', 'line'], ['خوردن', 'khordan', 'to eat']]],
  ];
  const start = new Date('2026-05-20T09:00:00Z').getTime();
  const now = new Date(start + 115 * DAY);
  const moments: SnapMoment[] = faScenes.map(([title], i) => ({ id: `demo-fa-${i}`, title, createdAt: new Date(start + i * 14 * DAY).toISOString() }));
  const byWord = new Map<string, SnapItem>();
  faScenes.forEach(([, words], i) => words.forEach(([ca, latin, en, kind, set], j) => {
    const key = `demo-fa:${ca}`;
    const seen = byWord.get(key);
    if (seen) { seen.momentIds.push(moments[i].id); return; }
    const r = rng(key);
    const age = (115 - i * 14) / 115;
    const worked = set === undefined ? r() < 0.35 + 0.6 * age : set > 0;
    const box = set ? set : worked ? Math.min(4, 1 + Math.floor(r() * 5 * age + 0.5)) : 0;
    const slipping = set === undefined && r() < 0.15;
    const practisedAt = worked ? new Date(now.getTime() - (slipping ? BOX_DAYS[box] * 2 + 3 : r() * BOX_DAYS[box]) * DAY) : null;
    const dueAt = new Date((practisedAt?.getTime() ?? now.getTime()) + BOX_DAYS[box] * DAY);
    byWord.set(key, {
      key, ca, en, latin, kind: (kind || (ca.includes(' ') ? 'expression' : 'vocab')) as Kind,
      box, learned: box >= 3, attempts: worked ? 2 + box * 2 : 0, passes: worked ? 1 + box : 0,
      practisedAt: practisedAt?.toISOString() ?? null, dueAt: dueAt.toISOString(),
      firstSeenAt: new Date(new Date(moments[i].createdAt).getTime() + j * 1000).toISOString(), momentIds: [moments[i].id],
      recalledIn: worked && (box >= 2 || r() < 0.5) ? [moments[i].id] : [],
    });
  }));
  // Shared words, one per bridge material: [word, box (0 = never practised), scenes recalled in].
  const shared: [string, number, number[]][] = [
    ['چنده؟', 4, [0, 3]], // forged and recalled in both moments: the Khaju bridge
    ['بفرمایید', 3, [2]], // forged, recalled in one: timber
    ['همین‌جا', 2, [1]], // recalled once: timber
    ['گل', 0, []], // heard only: rope
  ];
  for (const [ca, box, at] of shared) {
    const it = byWord.get(`demo-fa:${ca}`)!, b = box;
    const practisedAt = box ? new Date(now.getTime() - DAY) : null;
    Object.assign(it, {
      box: b, learned: b >= 3, attempts: box ? 2 + b * 2 : 0, passes: box ? 1 + b : 0, practisedAt: practisedAt?.toISOString() ?? null,
      dueAt: new Date((practisedAt?.getTime() ?? now.getTime()) + BOX_DAYS[b] * DAY).toISOString(), recalledIn: at.map((i) => moments[i].id),
    });
  }
  return { id: 'demo-fa', label: 'Persian demo learner (made up)', language: 'fa', now: now.toISOString(), moments, items: [...byWord.values()] };
}

// ---- words for the info card and legends ---------------------------------------------------------------

/** v1 materials: fog, then one per Leitner box (never shown as a number). */
export const STAGE_NAME = ['Fog', 'Scaffolding', 'Wood', 'Stone', 'Gold'] as const;
export const STAGE_MEANING = [
  'Heard in a moment; its first look is still to come.',
  'Met and passed once, not yet recalled after a gap.',
  'Recalled after a day. Getting there.',
  'Recalled after three days: forged. You can use it in battle.',
  'Recalled after a week. Solid.',
] as const;
export const KIND_NAME: Record<Kind, string> = { vocab: 'word', expression: 'expression', line: 'line from the moment', suggestion: 'reply you could say' };

export function ago(iso: string | null, now: Date): string {
  if (!iso) return 'never';
  const d = Math.floor((now.getTime() - new Date(iso).getTime()) / DAY);
  return d <= 0 ? 'today' : d === 1 ? 'yesterday' : `${d} days ago`;
}

/** Tiny stable colour per district: hue from the district's arrival order (golden angle), so neighbours differ. */
export function districtHue(d: District): number {
  return Math.round((d.order * 137.508 + 18) % 360);
}

/**
 * Event every variant dispatches when the learner taps something; the host shows the info card. A road is the
 * bridge between two districts (`a` and `b` in either order), for the words they share.
 */
export type Selection =
  | { kind: 'tile'; key: string }
  | { kind: 'district'; id: string }
  | { kind: 'road'; a: string; b: string }
  | null;
/** Whether a selected road joins districts a and b (a road is selected from either end). */
export const onRoad = (sel: { a: string; b: string }, a: string, b: string) =>
  (sel.a === a && sel.b === b) || (sel.a === b && sel.b === a);
export const select = (el: HTMLElement, sel: Selection) =>
  el.dispatchEvent(new CustomEvent<Selection>('town-select', { detail: sel, bubbles: true, composed: true }));

/** Whether two selections name the same thing. */
export const sameSelection = (x: Selection, y: Selection): boolean =>
  x === y || (!!x && !!y && x.kind === y.kind && (
    x.kind === 'tile' ? x.key === (y as { key: string }).key
    : x.kind === 'district' ? x.id === (y as { id: string }).id
    : onRoad(x, (y as { a: string }).a, (y as { b: string }).b)));

/**
 * Test hook for both looks: the point on a w × h canvas (CSS px) that `hit` reads as `sel` and that lies nearest
 * the middle of all such points, so a tap lands well inside a building rather than on its edge; null if none.
 */
export function pointOn(w: number, h: number, hit: (x: number, y: number) => Selection | undefined, sel: Selection, step = 3, eligible: (x: number, y: number) => boolean = () => true) {
  const pts: [number, number][] = [];
  for (let y = step; y < h; y += step) for (let x = step; x < w; x += step) if (sameSelection(hit(x, y) ?? null, sel) && eligible(x, y)) pts.push([x, y]);
  if (!pts.length) return null;
  const mx = pts.reduce((s, p) => s + p[0], 0) / pts.length, my = pts.reduce((s, p) => s + p[1], 0) / pts.length;
  const [x, y] = pts.reduce((a, p) => (Math.hypot(p[0] - mx, p[1] - my) < Math.hypot(a[0] - mx, a[1] - my) ? p : a));
  return { x, y };
}
